import { useEffect, useRef } from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConvState } from '../useScenarioPlayer';
import { tokenizeWords } from '../useScenarioPlayer';
import { UserBubble } from './UserBubble';
import { ToolCallLine } from './ToolCallLine';
import { ThinkingIndicator } from './ThinkingIndicator';
import { AssistantMessage } from './AssistantMessage';

interface ChatThreadProps {
  conv: ConvState;
  presentMode?: boolean;
  /** Clicking the thread while it plays fast-forwards it. */
  onSkip: () => void;
}

export const ChatThread = ({ conv, presentMode, onSkip }: ChatThreadProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // Auto-scroll only while the user is near the bottom; direct scrollTop (not
  // smooth) so frequent word-ticks don't queue jittery smooth-scrolls.
  useEffect(() => {
    const el = containerRef.current;
    if (el && conv.status === 'playing' && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [conv]);

  // Jump to top when opening a finished conversation from the list.
  useEffect(() => {
    const el = containerRef.current;
    if (el && conv.status === 'done') el.scrollTop = 0;
    stickToBottomRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.key]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      onClick={() => conv.status === 'playing' && onSkip()}
      className={cn(
        'flex-1 space-y-4 overflow-y-auto px-1 py-4',
        presentMode && 'text-lg leading-relaxed'
      )}
    >
      {conv.conversation.client && (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-500">
            <Building2 className="h-3 w-3 text-neutral-400" />
            {conv.conversation.client}
          </span>
        </div>
      )}

      {conv.steps.map((runStep, i) => {
        const { step } = runStep;
        if (step.kind === 'user') {
          // The bubble appears when the "typing pause" completes.
          if (runStep.phase !== 'done') return null;
          return <UserBubble key={i} content={step.content} time={runStep.time} />;
        }
        if (runStep.phase === 'pending') return null;
        if (step.kind === 'thinking') {
          return runStep.phase === 'running' ? (
            <ThinkingIndicator key={i} label={step.label} />
          ) : null;
        }
        if (step.kind === 'tool') {
          return <ToolCallLine key={i} step={step} phase={runStep.phase} />;
        }
        const streamedContent =
          runStep.phase === 'done'
            ? step.content
            : tokenizeWords(step.content).slice(0, runStep.wordsShown).join('');
        return (
          <AssistantMessage
            key={i}
            step={step}
            streamedContent={streamedContent}
            isStreaming={runStep.phase !== 'done'}
            time={runStep.time}
          />
        );
      })}
    </div>
  );
};
